import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { AppButton, AppTextField } from "./form";
import { setVpnOtpPromptListener, type VpnOtpRequest } from "../services/vpnOtpPrompt";
import { vpnSessionService, type VpnConnectResult } from "../services/vpnSessionService";

type Pending = { request: VpnOtpRequest; resolve: (connected: boolean) => void };
type Phase = "starting" | "code" | "submitting" | "error";

const backendMessage = (e: unknown): string =>
  (e as { response?: { data?: { response?: { message?: string } } } })?.response?.data?.response?.message ||
  (e instanceof Error ? e.message : String(e));

/**
 * App-wide dialog that completes a VPN login needing extra verification. Opening it starts the connection; when the
 * server asks for a code (one it e-mailed or texted, or an authenticator code) its own message is shown and the
 * user's answer continues the same login. Mounted once; requests come through requestVpnOtp().
 */
export default function VpnOtpDialogHost() {
  const { t } = useTranslation();
  const [queue, setQueue] = useState<Pending[]>([]);
  const [phase, setPhase] = useState<Phase>("starting");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setVpnOtpPromptListener((request, resolve) => setQueue((prev) => [...prev, { request, resolve }]));
    return () => setVpnOtpPromptListener(null);
  }, []);

  const current = queue[0];
  const profileId = current?.request.vpnProfileId;

  const finish = useCallback(
    (connected: boolean) => {
      if (!current) return;
      current.resolve(connected);
      setQueue((prev) => prev.slice(1));
    },
    [current]
  );

  const handleStep = useCallback(
    (result: VpnConnectResult) => {
      if (result.status === "CONNECTED") {
        finish(true);
        return;
      }
      setChallengeId(result.challengeId ?? null);
      setPrompt(result.prompt ?? null);
      setCode("");
      setError(null);
      setPhase("code");
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [finish]
  );

  const start = useCallback(async () => {
    if (!profileId) return;
    setPhase("starting");
    setError(null);
    setPrompt(null);
    setChallengeId(null);
    try {
      handleStep(await vpnSessionService.connect(profileId));
    } catch (e: unknown) {
      setError(backendMessage(e) || t("vpnOtp.failed"));
      setPhase("error");
    }
  }, [profileId, handleStep, t]);

  // Each new request starts its login right away (this is what makes the server send an e-mailed code).
  useEffect(() => {
    if (profileId) void start();
  }, [profileId, start]);

  const submit = async () => {
    if (!profileId || !challengeId || !code.trim()) return;
    setPhase("submitting");
    setError(null);
    try {
      handleStep(await vpnSessionService.answer(profileId, challengeId, code.trim()));
    } catch (e: unknown) {
      setError(backendMessage(e) || t("vpnOtp.failed"));
      setPhase("error");
    }
  };

  const cancel = () => {
    if (profileId && phase !== "error") {
      void vpnSessionService.cancel(profileId).catch(() => undefined);
    }
    finish(false);
  };

  const busy = phase === "starting" || phase === "submitting";

  return (
    <Dialog open={!!current} onClose={() => !busy && cancel()} maxWidth="xs" fullWidth>
      <DialogTitle>{t("vpnOtp.title", { name: current?.request.vpnProfileName || "VPN" })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {phase === "starting" && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">{t("vpnOtp.starting")}</Typography>
            </Stack>
          )}
          {(phase === "code" || phase === "submitting") && (
            <>
              <Alert severity="info" variant="outlined">
                {prompt || t("vpnOtp.genericPrompt")}
              </Alert>
              <AppTextField
                inputRef={inputRef}
                autoFocus
                label={t("vpnOtp.codeLabel")}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void submit();
                }}
                autoComplete="one-time-code"
                inputProps={{ maxLength: 32 }}
                disabled={phase === "submitting"}
                fullWidth
              />
            </>
          )}
          {phase === "error" && error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <AppButton variant="outlined" onClick={cancel} disabled={busy}>
          {t("common.cancel")}
        </AppButton>
        {phase === "error" ? (
          <AppButton variant="contained" onClick={() => void start()}>
            {t("vpnOtp.restart")}
          </AppButton>
        ) : (
          <AppButton
            variant="contained"
            onClick={() => void submit()}
            disabled={phase !== "code" || !code.trim()}
          >
            {phase === "submitting" ? t("vpnOtp.connecting") : t("vpnOtp.connect")}
          </AppButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
